import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as api from '../../api';
import Section from '../Atoms/Section';
import Spinner from '../Atoms/Spinner';
import PieceMediaUpload from '../Components/PieceMediaUpload';
import { FD100MultiSelectOptions } from '../Utils/Listings';
import { createUrlWithRedirect, storeRedirect } from '../Utils/redirectUtils';
import BoldBlue from '../Atoms/BoldBlue';

const FormContainer = styled(Section)`
  padding: 40px 20px;
  max-width: 1000px;
  margin: 0 auto;
  background-color: #f5f5f5;
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 2.5rem;
  font-size: 2.5rem;
  color: #333;
  font-weight: 600;
`;

const FormSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  padding-bottom: 20px;
  margin-bottom: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
`;

const SectionTitle = styled.h2`
  margin-bottom: 1.5rem;
  color: #1a1a1a;
  border-bottom: 3px solid #0000FF;
  padding-bottom: 10px;
  font-size: 1.6rem;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 15px;
`;

const RequiredMark = styled.span`
  color: #dc3545;
  margin-left: 4px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  transition: all 0.3s ease;
  background-color: #fafafa;

  &:focus {
    outline: none;
    border-color: #0000FF;
    background-color: white;
    box-shadow: 0 0 0 3px rgba(0, 0, 255, 0.1);
  }

  &:hover {
    border-color: #ccc;
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  transition: all 0.3s ease;
  background-color: #fafafa;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23333' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 15px center;
  padding-right: 40px;

  &:focus {
    outline: none;
    border-color: #0000FF;
    background-color: white;
    box-shadow: 0 0 0 3px rgba(0, 0, 255, 0.1);
  }

  &:hover {
    border-color: #ccc;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  resize: vertical;
  min-height: ${props => props.rows ? props.rows * 25 + 'px' : '100px'};
  font-family: inherit;
  line-height: 1.6;
  transition: all 0.3s ease;
  background-color: #fafafa;

  &:focus {
    outline: none;
    border-color: #0000FF;
    background-color: white;
    box-shadow: 0 0 0 3px rgba(0, 0, 255, 0.1);
  }

  &:hover {
    border-color: #ccc;
  }
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 13px;
  margin-top: 5px;
  display: block;
`;

const PieceCard = styled.div`
  background: #f8f9fa;
  border-radius: 10px;
  padding: 25px;
  margin-bottom: 20px;
  position: relative;
  border: 2px solid #e8e8e8;
  transition: all 0.3s ease;

  &:hover {
    border-color: #0000FF;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const PieceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.isExpanded ? '20px' : '0'};
  cursor: pointer;
  padding: 15px;
  margin: -15px -15px ${props => props.isExpanded ? '20px' : '-15px'} -15px;
  background: ${props => props.isExpanded ? 'transparent' : '#f8f9fa'};
  border-radius: ${props => props.isExpanded ? '0' : '8px'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.isExpanded ? 'transparent' : '#e9ecef'};
  }
`;

const ExpandIcon = styled.span`
  display: inline-block;
  margin-right: 10px;
  transition: transform 0.3s ease;
  transform: ${props => props.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  font-size: 14px;
  color: #666;
`;

const PieceContent = styled.div`
  display: ${props => props.isExpanded ? 'block' : 'none'};
`;

const PieceName = styled.span`
  color: #666;
  font-style: italic;
  margin-left: 10px;
  font-size: 14px;
`;

// Preview Modal Styles
const PreviewModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const PreviewContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 40px;
  position: relative;
`;

const PreviewHeader = styled.div`
  margin-bottom: 30px;
  border-bottom: 2px solid #0000FF;
  padding-bottom: 20px;
`;

const PreviewTitle = styled.h2`
  color: #333;
  margin-bottom: 10px;
`;

const WarningBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 25px;
  color: #856404;
  font-size: 15px;
  
  strong {
    display: block;
    margin-bottom: 5px;
  }
`;

const PreviewSection = styled.div`
  margin-bottom: 30px;
`;

const PreviewSectionTitle = styled.h3`
  color: #0000FF;
  margin-bottom: 20px;
  font-size: 1.3rem;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 15px;
  margin-bottom: 15px;
`;

const PreviewLabel = styled.div`
  font-weight: 600;
  color: #555;
`;

const PreviewValue = styled.div`
  color: #333;
  white-space: pre-wrap;
`;

const PreviewPieceCard = styled.div`
  background: #f8f9fa;
  padding: 25px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
`;

const PreviewMediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 15px;
`;

const PreviewMediaItem = styled.div`
  text-align: center;
  
  img {
    width: 100%;
    height: 100px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #ddd;
  }
  
  .media-name {
    font-size: 12px;
    color: #666;
    margin-top: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const PreviewButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid #e0e0e0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const PieceTitle = styled.h4`
  margin: 0;
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
`;

const RemoveButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;

  &:hover {
    background: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
  }
`;

const AddButton = styled.button`
  background: #0000FF;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #0000CC;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 255, 0.2);
  }

  &:before {
    content: "+";
    font-size: 18px;
    font-weight: 600;
  }
`;

const SubmitButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  font-size: 1.1rem;
  padding: 16px 40px;
  margin-top: 30px;
  width: 100%;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #218838;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const Alert = styled.div`
  padding: 16px 20px;
  margin-bottom: 25px;
  border-radius: 8px;
  font-size: 15px;
  
  ${props => props.variant === 'success' && `
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `}
  
  ${props => props.variant === 'danger' && `
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
  
  ${props => props.variant === 'info' && `
    background-color: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
  `}
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const InfoBlock = styled.div`
  background: #f0f4ff;
  border: 1px solid #d0d8ff;
  border-radius: 8px;
  padding: 20px;
  padding-bottom: 0px;
  margin-bottom: 20px;
`;

const BlockTitle = styled.h3`
  color: #0000FF;
  font-size: 1.2rem;
  margin-bottom: 15px;
  font-weight: 600;
`;

const ExplanationText = styled.div`
  font-size: 13px;
  color: #666;
  background: #f8f9fa;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  line-height: 1.5;
  border-left: 3px solid #0000FF;
`;

const ReadOnlyField = styled.div`
  padding: 12px 15px;
  background: #e9ecef;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 15px;
  color: #495057;
  font-weight: 500;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 30px;
  padding: 20px;
  background: #fffbf0;
  border: 2px solid #ffd700;
  border-radius: 8px;
`;

const Checkbox = styled.input`
  margin-right: 10px;
  margin-top: 3px;
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  cursor: pointer;
  flex: 1;
`;

const Milan2026Form = ({ fullUser, fullUserLoading }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const [formData, setFormData] = useState({
    // Contact du candidat
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    userType: '', // 'designer' or 'fabricant'
    
    // Designer block (if userType === 'designer')
    designerCompanyName: '',
    designerInternationalPresence: '',
    designerInternationalDetails: '',
    designerWebAndSocial: '',
    designerWebAndSocialDetails: '',
    designerPresentation: '',
    
    // Fabricant/Éditeur block (if userType === 'fabricant')
    fabricantCompanyName: '',
    fabricantInternationalPresence: '',
    fabricantInternationalDetails: '',
    fabricantWebAndSocial: '',
    fabricantWebAndSocialDetails: '',
    fabricantPresentation: '',
    fabricantCodifab: '',
    fabricantAmeublement: '',
    
    pieces: [],
    certificationAccepted: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [expandedPieces, setExpandedPieces] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [validationError, setValidationError] = useState('');

  const { typologieObjetFD100 } = FD100MultiSelectOptions;
  const [certificationAccepted, setCertificationAccepted] = useState(false);
  
  // Refs for form fields
  const fieldRefs = useRef({
    email: null,
    firstName: null,
    lastName: null,
    phoneNumber: null,
    userType: null,
    designerCompanyName: null,
    fabricantCompanyName: null
  });

  useEffect(() => {
    if (fullUser && !fullUserLoading) {
      const companyName = fullUser.hsCompany?.properties?.name || fullUser.company?.name || '';
      const hasWebPresence = fullUser.hsCompany?.properties?.website || 
          fullUser.hsCompany?.properties?.compte_instagram || 
          fullUser.hsCompany?.properties?.twitterhandle || 
          fullUser.hsCompany?.properties?.linkedin_company_page;
      
      const webDetails = [
        fullUser.hsCompany?.properties?.website,
        fullUser.hsCompany?.properties?.compte_instagram ? `Instagram: ${fullUser.hsCompany.properties.compte_instagram}` : null,
        fullUser.hsCompany?.properties?.twitterhandle ? `Twitter: ${fullUser.hsCompany.properties.twitterhandle}` : null,
        fullUser.hsCompany?.properties?.linkedin_company_page ? `LinkedIn: ${fullUser.hsCompany.properties.linkedin_company_page}` : null
      ].filter(Boolean).join(', ');
      
      const companyPresentation = isEn ? fullUser.hsCompany?.properties?.description_courte_en : fullUser.hsCompany?.properties?.description_courte_fr;
      
      // Prefill form with user data
      setFormData(prev => ({
        ...prev,
        // Contact info
        email: fullUser.hsProfile?.email || fullUser.user?.email || '',
        firstName: fullUser.hsProfile?.firstname || '',
        lastName: fullUser.hsProfile?.lastname || '',
        phoneNumber: fullUser.hsProfile?.mobilephone || fullUser.hsProfile?.phone || '',
        
        // If we can detect user type from their profile, prefill designer or fabricant data
        designerCompanyName: companyName,
        designerWebAndSocial: hasWebPresence ? 'oui' : '',
        designerWebAndSocialDetails: webDetails,
        designerPresentation: companyPresentation || '',
        
        fabricantCompanyName: companyName,
        fabricantWebAndSocial: hasWebPresence ? 'oui' : '',
        fabricantWebAndSocialDetails: webDetails,
        fabricantPresentation: companyPresentation || '',
        fabricantCodifab: fullUser.hsProfile?.adherent && fullUser.hsProfile.adherent.includes("CODIFAB") ? 'oui' : '',
        fabricantAmeublement: fullUser.hsProfile?.adherent && fullUser.hsProfile.adherent.includes("Ameublement Français") ? 'oui' : ''
      }));
    }
  }, [fullUser, fullUserLoading, isEn]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  const handlePieceChange = (index, field, value) => {
    const updatedPieces = [...formData.pieces];
    updatedPieces[index] = {
      ...updatedPieces[index],
      [field]: value
    };
    setFormData(prev => ({ ...prev, pieces: updatedPieces }));
  };

  const handlePieceMediaChange = (index, media) => {
    const updatedPieces = [...formData.pieces];
    updatedPieces[index] = {
      ...updatedPieces[index],
      media: media
    };
    setFormData(prev => ({ ...prev, pieces: updatedPieces }));
  };

  const addPiece = () => {
    if (formData.pieces.length < 3) {
      const newIndex = formData.pieces.length;
      setFormData(prev => ({
        ...prev,
        pieces: [...prev.pieces, {
          // Designer info for the piece
          designerCompanyName: '',
          designerInternationalPresence: '',
          designerInternationalDetails: '',
          designerWebAndSocial: '',
          designerWebAndSocialDetails: '',
          designerPresentation: '',
          
          // Fabricant info for the piece
          fabricantCompanyName: '',
          fabricantInternationalPresence: '',
          fabricantInternationalDetails: '',
          fabricantWebAndSocial: '',
          fabricantWebAndSocialDetails: '',
          fabricantPresentation: '',
          fabricantCodifab: '',
          fabricantAmeublement: '',
          
          // General piece info
          name: '',
          typologie: '',
          typologieOther: '',
          dimensions: '',
          materials: '',
          poids: '',
          creationDate: '',
          savoirFaire: '',
          durability: '',
          presentationText: '',
          media: []
        }]
      }));
      // Expand the new piece by default
      setExpandedPieces(prev => ({ ...prev, [newIndex]: true }));
    }
  };

  const togglePieceExpansion = (index) => {
    setExpandedPieces(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const getTypologyLabel = (value) => {
    if (!value) return '';
    
    // Check in object typologies
    const objetType = typologieObjetFD100(i18n.language).find(t => t.value === value);
    if (objetType) return objetType.label;
    
    return value;
  };

  const removePiece = (index) => {
    setFormData(prev => ({
      ...prev,
      pieces: prev.pieces.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Contact fields
    if (!formData.firstName) newErrors.firstName = isEn ? 'First name required' : 'Prénom requis';
    if (!formData.lastName) newErrors.lastName = isEn ? 'Last name required' : 'Nom requis';
    if (!formData.email) newErrors.email = isEn ? 'Email required' : 'Email requis';
    if (!formData.phoneNumber) newErrors.phoneNumber = isEn ? 'Phone number required' : 'Numéro de téléphone requis';
    if (!formData.userType) newErrors.userType = isEn ? 'Please select designer or manufacturer/publisher' : 'Veuillez sélectionner designer ou fabricant/éditeur';

    // Validate designer or fabricant block based on userType
    if (formData.userType === 'designer') {
      if (!formData.designerCompanyName) newErrors.designerCompanyName = isEn ? 'Company name required' : 'Nom de l\'entreprise requis';
      if (!formData.designerInternationalPresence) newErrors.designerInternationalPresence = isEn ? 'Field required' : 'Champ requis';
      if (formData.designerInternationalPresence === 'oui' && !formData.designerInternationalDetails) {
        newErrors.designerInternationalDetails = isEn ? 'Please specify' : 'Précisez';
      }
      if (!formData.designerWebAndSocial) newErrors.designerWebAndSocial = isEn ? 'Field required' : 'Champ requis';
      if (formData.designerWebAndSocial === 'oui' && !formData.designerWebAndSocialDetails) {
        newErrors.designerWebAndSocialDetails = isEn ? 'Please specify' : 'Précisez';
      }
      if (!formData.designerPresentation) newErrors.designerPresentation = isEn ? 'Presentation required' : 'Présentation requise';
    } else if (formData.userType === 'fabricant') {
      if (!formData.fabricantCompanyName) newErrors.fabricantCompanyName = isEn ? 'Company name required' : 'Nom de l\'entreprise requis';
      if (!formData.fabricantInternationalPresence) newErrors.fabricantInternationalPresence = isEn ? 'Field required' : 'Champ requis';
      if (formData.fabricantInternationalPresence === 'oui' && !formData.fabricantInternationalDetails) {
        newErrors.fabricantInternationalDetails = isEn ? 'Please specify' : 'Précisez';
      }
      if (!formData.fabricantWebAndSocial) newErrors.fabricantWebAndSocial = isEn ? 'Field required' : 'Champ requis';
      if (formData.fabricantWebAndSocial === 'oui' && !formData.fabricantWebAndSocialDetails) {
        newErrors.fabricantWebAndSocialDetails = isEn ? 'Please specify' : 'Précisez';
      }
      if (!formData.fabricantPresentation) newErrors.fabricantPresentation = isEn ? 'Presentation required' : 'Présentation requise';
      if (!formData.fabricantCodifab) newErrors.fabricantCodifab = isEn ? 'Field required' : 'Champ requis';
      if (!formData.fabricantAmeublement) newErrors.fabricantAmeublement = isEn ? 'Field required' : 'Champ requis';
    }

    if (formData.pieces.length === 0) {
      newErrors.pieces = isEn ? 'At least one piece is required' : 'Au moins une pièce est requise';
    }

    formData.pieces.forEach((piece, index) => {
      // Validate designer block for piece (if user is not designer)
      if (formData.userType !== 'designer') {
        if (!piece.designerCompanyName) newErrors[`piece_${index}_designerCompanyName`] = isEn ? 'Designer company required' : 'Entreprise du designer requise';
        if (!piece.designerInternationalPresence) newErrors[`piece_${index}_designerInternationalPresence`] = isEn ? 'Field required' : 'Champ requis';
        if (piece.designerInternationalPresence === 'oui' && !piece.designerInternationalDetails) {
          newErrors[`piece_${index}_designerInternationalDetails`] = isEn ? 'Please specify' : 'Précisez';
        }
        if (!piece.designerWebAndSocial) newErrors[`piece_${index}_designerWebAndSocial`] = isEn ? 'Field required' : 'Champ requis';
        if (piece.designerWebAndSocial === 'oui' && !piece.designerWebAndSocialDetails) {
          newErrors[`piece_${index}_designerWebAndSocialDetails`] = isEn ? 'Please specify' : 'Précisez';
        }
        if (!piece.designerPresentation) newErrors[`piece_${index}_designerPresentation`] = isEn ? 'Presentation required' : 'Présentation requise';
      }

      // Validate fabricant block for piece (if user is not fabricant)
      if (formData.userType !== 'fabricant') {
        if (!piece.fabricantCompanyName) newErrors[`piece_${index}_fabricantCompanyName`] = isEn ? 'Manufacturer company required' : 'Entreprise du fabricant requise';
        if (!piece.fabricantInternationalPresence) newErrors[`piece_${index}_fabricantInternationalPresence`] = isEn ? 'Field required' : 'Champ requis';
        if (piece.fabricantInternationalPresence === 'oui' && !piece.fabricantInternationalDetails) {
          newErrors[`piece_${index}_fabricantInternationalDetails`] = isEn ? 'Please specify' : 'Précisez';
        }
        if (!piece.fabricantWebAndSocial) newErrors[`piece_${index}_fabricantWebAndSocial`] = isEn ? 'Field required' : 'Champ requis';
        if (piece.fabricantWebAndSocial === 'oui' && !piece.fabricantWebAndSocialDetails) {
          newErrors[`piece_${index}_fabricantWebAndSocialDetails`] = isEn ? 'Please specify' : 'Précisez';
        }
        if (!piece.fabricantPresentation) newErrors[`piece_${index}_fabricantPresentation`] = isEn ? 'Presentation required' : 'Présentation requise';
        if (!piece.fabricantCodifab) newErrors[`piece_${index}_fabricantCodifab`] = isEn ? 'Field required' : 'Champ requis';
        if (!piece.fabricantAmeublement) newErrors[`piece_${index}_fabricantAmeublement`] = isEn ? 'Field required' : 'Champ requis';
      }

      // General piece info
      if (!piece.name) newErrors[`piece_${index}_name`] = isEn ? 'Name required' : 'Nom requis';
      if (!piece.typologie) newErrors[`piece_${index}_typologie`] = isEn ? 'Typology required' : 'Typologie requise';
      if (piece.typologie === 'autre' && !piece.typologieOther) newErrors[`piece_${index}_typologieOther`] = isEn ? 'Please specify' : 'Veuillez préciser';
      if (!piece.dimensions) newErrors[`piece_${index}_dimensions`] = isEn ? 'Dimensions required' : 'Dimensions requises';
      if (!piece.materials) newErrors[`piece_${index}_materials`] = isEn ? 'Materials required' : 'Matériaux requis';
      if (!piece.creationDate) newErrors[`piece_${index}_creationDate`] = isEn ? 'Date required' : 'Date requise';
      if (!piece.savoirFaire) newErrors[`piece_${index}_savoirFaire`] = isEn ? 'Craftsmanship required' : 'Savoir-faire requis';
      if (!piece.durability) newErrors[`piece_${index}_durability`] = isEn ? 'Sustainability required' : 'Durabilité requise';
      if (!piece.presentationText) newErrors[`piece_${index}_presentationText`] = isEn ? 'Presentation text required' : 'Texte de présentation requis';
      if (!piece.media || piece.media.length === 0) newErrors[`piece_${index}_media`] = isEn ? 'At least one media is required' : 'Au moins un média est requis';
      
      // Check if all media have credits
      if (piece.media && piece.media.length > 0) {
        const missingCredits = piece.media.some(m => !m.credit || m.credit.trim() === '');
        if (missingCredits) {
          newErrors[`piece_${index}_media_credits`] = isEn ? 'All media must have credits' : 'Tous les médias doivent avoir des crédits';
        }
      }
    });

    // Check certification checkbox
    if (!certificationAccepted) {
      newErrors.certification = isEn ? 'You must accept the terms' : 'Vous devez accepter les conditions';
    }

    setErrors(newErrors);
    
    // If there are errors, set validation error message and scroll to top
    if (Object.keys(newErrors).length > 0) {
      setValidationError(isEn ? 'Please complete all required fields' : 'Veuillez compléter tous les champs requis');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setValidationError('');
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handlePreview = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Re-run validation to get all errors synchronously
      const allErrors = {};
      
      // Contact fields validation
      if (!formData.firstName) allErrors.firstName = isEn ? 'First name required' : 'Prénom requis';
      if (!formData.lastName) allErrors.lastName = isEn ? 'Last name required' : 'Nom requis';
      if (!formData.email) allErrors.email = isEn ? 'Email required' : 'Email requis';
      if (!formData.phoneNumber) allErrors.phoneNumber = isEn ? 'Phone number required' : 'Numéro de téléphone requis';
      if (!formData.userType) allErrors.userType = isEn ? 'Please select designer or manufacturer/publisher' : 'Veuillez sélectionner designer ou fabricant/éditeur';
      
      // Designer or fabricant block validation based on userType
      if (formData.userType === 'designer') {
        if (!formData.designerCompanyName) allErrors.designerCompanyName = isEn ? 'Company name required' : 'Nom de l\'entreprise requis';
        if (!formData.designerInternationalPresence) allErrors.designerInternationalPresence = isEn ? 'Field required' : 'Champ requis';
        if (formData.designerInternationalPresence === 'oui' && !formData.designerInternationalDetails) {
          allErrors.designerInternationalDetails = isEn ? 'Please specify' : 'Précisez';
        }
        if (!formData.designerWebAndSocial) allErrors.designerWebAndSocial = isEn ? 'Field required' : 'Champ requis';
        if (formData.designerWebAndSocial === 'oui' && !formData.designerWebAndSocialDetails) {
          allErrors.designerWebAndSocialDetails = isEn ? 'Please specify' : 'Précisez';
        }
        if (!formData.designerPresentation) allErrors.designerPresentation = isEn ? 'Presentation required' : 'Présentation requise';
      } else if (formData.userType === 'fabricant') {
        if (!formData.fabricantCompanyName) allErrors.fabricantCompanyName = isEn ? 'Company name required' : 'Nom de l\'entreprise requis';
        if (!formData.fabricantInternationalPresence) allErrors.fabricantInternationalPresence = isEn ? 'Field required' : 'Champ requis';
        if (formData.fabricantInternationalPresence === 'oui' && !formData.fabricantInternationalDetails) {
          allErrors.fabricantInternationalDetails = isEn ? 'Please specify' : 'Précisez';
        }
        if (!formData.fabricantWebAndSocial) allErrors.fabricantWebAndSocial = isEn ? 'Field required' : 'Champ requis';
        if (formData.fabricantWebAndSocial === 'oui' && !formData.fabricantWebAndSocialDetails) {
          allErrors.fabricantWebAndSocialDetails = isEn ? 'Please specify' : 'Précisez';
        }
        if (!formData.fabricantPresentation) allErrors.fabricantPresentation = isEn ? 'Presentation required' : 'Présentation requise';
        if (!formData.fabricantCodifab) allErrors.fabricantCodifab = isEn ? 'Field required' : 'Champ requis';
        if (!formData.fabricantAmeublement) allErrors.fabricantAmeublement = isEn ? 'Field required' : 'Champ requis';
      }
      
      // Pieces validation
      if (formData.pieces.length === 0) {
        allErrors.pieces = isEn ? 'At least one piece is required' : 'Au moins une pièce est requise';
      }
      
      let firstPieceErrorIndex = -1;
      formData.pieces.forEach((piece, index) => {
        if (!piece.name) allErrors[`piece_${index}_name`] = isEn ? 'Name required' : 'Nom requis';
        if (!piece.typologie) allErrors[`piece_${index}_typologie`] = isEn ? 'Typology required' : 'Typologie requise';
        if (piece.typologie === 'autre' && !piece.typologieOther) allErrors[`piece_${index}_typologieOther`] = isEn ? 'Please specify' : 'Veuillez préciser';
        if (!piece.creationDate) allErrors[`piece_${index}_creationDate`] = isEn ? 'Date required' : 'Date requise';
        
        // Validate designer block for piece (if user is not designer)
        if (formData.userType !== 'designer') {
          if (!piece.designerCompanyName) allErrors[`piece_${index}_designerCompanyName`] = isEn ? 'Designer company required' : 'Entreprise du designer requise';
          if (!piece.designerInternationalPresence) allErrors[`piece_${index}_designerInternationalPresence`] = isEn ? 'Field required' : 'Champ requis';
          if (piece.designerInternationalPresence === 'oui' && !piece.designerInternationalDetails) {
            allErrors[`piece_${index}_designerInternationalDetails`] = isEn ? 'Please specify' : 'Précisez';
          }
          if (!piece.designerWebAndSocial) allErrors[`piece_${index}_designerWebAndSocial`] = isEn ? 'Field required' : 'Champ requis';
          if (piece.designerWebAndSocial === 'oui' && !piece.designerWebAndSocialDetails) {
            allErrors[`piece_${index}_designerWebAndSocialDetails`] = isEn ? 'Please specify' : 'Précisez';
          }
          if (!piece.designerPresentation) allErrors[`piece_${index}_designerPresentation`] = isEn ? 'Presentation required' : 'Présentation requise';
        }

        // Validate fabricant block for piece (if user is not fabricant)
        if (formData.userType !== 'fabricant') {
          if (!piece.fabricantCompanyName) allErrors[`piece_${index}_fabricantCompanyName`] = isEn ? 'Manufacturer company required' : 'Entreprise du fabricant requise';
          if (!piece.fabricantInternationalPresence) allErrors[`piece_${index}_fabricantInternationalPresence`] = isEn ? 'Field required' : 'Champ requis';
          if (piece.fabricantInternationalPresence === 'oui' && !piece.fabricantInternationalDetails) {
            allErrors[`piece_${index}_fabricantInternationalDetails`] = isEn ? 'Please specify' : 'Précisez';
          }
          if (!piece.fabricantWebAndSocial) allErrors[`piece_${index}_fabricantWebAndSocial`] = isEn ? 'Field required' : 'Champ requis';
          if (piece.fabricantWebAndSocial === 'oui' && !piece.fabricantWebAndSocialDetails) {
            allErrors[`piece_${index}_fabricantWebAndSocialDetails`] = isEn ? 'Please specify' : 'Précisez';
          }
          if (!piece.fabricantPresentation) allErrors[`piece_${index}_fabricantPresentation`] = isEn ? 'Presentation required' : 'Présentation requise';
          if (!piece.fabricantCodifab) allErrors[`piece_${index}_fabricantCodifab`] = isEn ? 'Field required' : 'Champ requis';
          if (!piece.fabricantAmeublement) allErrors[`piece_${index}_fabricantAmeublement`] = isEn ? 'Field required' : 'Champ requis';
        }
        
        if (!piece.savoirFaire) allErrors[`piece_${index}_savoirFaire`] = isEn ? 'Craftsmanship required' : 'Savoir-faire requis';
        if (!piece.durability) allErrors[`piece_${index}_durability`] = isEn ? 'Sustainability required' : 'Durabilité requise';
        if (!piece.presentationText) allErrors[`piece_${index}_presentationText`] = isEn ? 'Presentation text required' : 'Texte de présentation requis';
        if (!piece.dimensions) allErrors[`piece_${index}_dimensions`] = isEn ? 'Dimensions required' : 'Dimensions requises';
        if (!piece.materials) allErrors[`piece_${index}_materials`] = isEn ? 'Materials required' : 'Matériaux requis';
        if (!piece.media || piece.media.length === 0) allErrors[`piece_${index}_media`] = isEn ? 'At least one media is required' : 'Au moins un média est requis';
        // Check if all media have credits
        if (piece.media && piece.media.length > 0) {
          const missingCredits = piece.media.some(m => !m.credit || m.credit.trim() === '');
          if (missingCredits) {
            allErrors[`piece_${index}_media_credits`] = isEn ? 'All media must have credits' : 'Tous les médias doivent avoir des crédits';
          }
        }
        
        // Track first piece with error
        if (firstPieceErrorIndex === -1) {
          const pieceErrors = Object.keys(allErrors).filter(key => key.startsWith(`piece_${index}_`));
          if (pieceErrors.length > 0) {
            firstPieceErrorIndex = index;
          }
        }
      });
      
      // Find first error field
      const errorKeys = Object.keys(allErrors);
      const firstErrorKey = errorKeys[0];
      
      // Check if it's a piece field error
      const pieceMatch = firstErrorKey?.match(/^piece_(\d+)_/);
      if (pieceMatch) {
        const pieceIndex = parseInt(pieceMatch[1]);
        
        // Expand the piece if it's collapsed
        if (!expandedPieces[pieceIndex]) {
          setExpandedPieces(prev => ({ ...prev, [pieceIndex]: true }));
        }
        
        // Wait for expansion animation then scroll to piece section
        setTimeout(() => {
          const pieceCard = document.querySelectorAll('.piece-card')[pieceIndex];
          if (pieceCard) {
            pieceCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      } else if (firstErrorKey === 'pieces') {
        // Scroll to add piece button if no pieces
        const addButton = document.querySelector('.add-piece-button');
        if (addButton) {
          addButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else if (fieldRefs.current[firstErrorKey]) {
        // Scroll to regular field
        fieldRefs.current[firstErrorKey].scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        setTimeout(() => {
          fieldRefs.current[firstErrorKey].focus();
        }, 500);
      } else {
        // Fallback to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    // Show preview modal
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setShowPreview(false);

    try {
      // Create FormData object to handle file uploads
      const submitData = new FormData();
      
      // Add all non-file data as JSON
      const jsonData = {
        ...formData,
        pieces: formData.pieces.map(piece => {
          const { media, ...pieceData } = piece;
          // Keep media metadata (including credits) but not the file objects
          return {
            ...pieceData,
            media: media.map(m => ({ credit: m.credit }))
          };
        })
      };
      submitData.append('data', JSON.stringify(jsonData));
      
      // Add all media files
      formData.pieces.forEach((piece, pieceIndex) => {
        piece.media.forEach((mediaItem, mediaIndex) => {
          submitData.append(`piece_${pieceIndex}_media_${mediaIndex}`, mediaItem.file);
        });
      });
      
      await api.submitMilan2026Form(submitData);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Reset form after successful submission
      setCertificationAccepted(false);
      setFormData({
        // Contact du candidat
        firstName: fullUser?.hsProfile?.firstname || '',
        lastName: fullUser?.hsProfile?.lastname || '',
        email: fullUser?.hsProfile?.email || fullUser?.user?.email || '',
        phoneNumber: fullUser?.hsProfile?.mobilephone || fullUser?.hsProfile?.phone || '',
        userType: '',
        
        // Designer block fields
        designerCompanyName: '',
        designerInternationalPresence: '',
        designerInternationalDetails: '',
        designerWebAndSocial: '',
        designerWebAndSocialDetails: '',
        designerPresentation: '',
        
        // Fabricant/Éditeur block fields
        fabricantCompanyName: '',
        fabricantInternationalPresence: '',
        fabricantInternationalDetails: '',
        fabricantWebAndSocial: '',
        fabricantWebAndSocialDetails: '',
        fabricantPresentation: '',
        fabricantCodifab: '',
        fabricantAmeublement: '',
        
        pieces: [],
        certificationAccepted: false
      });
      setExpandedPieces({});
    } catch (err) {
      setError(err.response?.data?.message || (isEn ? 'An error occurred' : 'Une erreur est survenue'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  // Event is over
  return (
    <FormContainer>
      <Title>{isEn ? 'Milan 2026 Project' : 'Projet Milan 2026'}</Title>
      <Alert variant="info">
        {isEn 
          ? 'Registration for the Milan 2026 project is now closed.'
          : "L'inscription au projet Milan 2026 est maintenant fermée."}
      </Alert>
    </FormContainer>
  );

  if (fullUserLoading) {
    return (
      <FormContainer>
        <Title>{isEn ? 'Milan 2026 Project' : 'Projet Milan 2026'}</Title>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spinner />
        </div>
      </FormContainer>
    );
  }

  // Check if user is logged in
  if (!fullUser || !fullUser.user) {
    return (
      <FormContainer>
        <Title>{isEn ? 'Milan 2026 Project' : 'Projet Milan 2026'}</Title>
        <Alert variant="info">
          {isEn 
            ? 'Please log in to access this form.'
            : 'Veuillez vous connecter pour accéder à ce formulaire.'}
        </Alert>
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            style={{
              background: '#0000FF',
              color: 'white',
              border: 'none',
              padding: '12px 30px',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
            onClick={() => navigate(createUrlWithRedirect('/login', '/forms/milan-2026'))}
          >
            {isEn ? 'Go to login' : 'Aller à la connexion'}
          </button>
        </div>
      </FormContainer>
    );
  }

  // Check if user has completed profile and company registration
  if (!fullUser.user.companyHubspotID) {
    return (
      <FormContainer>
        <Title>{isEn ? 'Milan 2026 Project' : 'Projet Milan 2026'}</Title>
        <Alert variant="info">
          {isEn 
            ? 'You must complete your company registration before accessing this form.'
            : 'Vous devez compléter l\'enregistrement de votre entreprise avant d\'accéder à ce formulaire.'}
        </Alert>
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            style={{
              background: '#0000FF',
              color: 'white',
              border: 'none',
              padding: '12px 30px',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
            onClick={() => {
              storeRedirect('/forms/milan-2026');
              navigate('/mycompany');
            }}
          >
            {isEn ? 'Complete company registration' : 'Compléter l\'enregistrement de l\'entreprise'}
          </button>
        </div>
      </FormContainer>
    );
  }

  // Component to render designer block
  const renderDesignerBlock = (prefix = 'designer', piece = null, pieceIndex = null) => {
    const getValue = (field) => piece ? piece[`${prefix}${field}`] : formData[`${prefix}${field}`];
    const getFieldName = (field) => pieceIndex !== null ? `piece_${pieceIndex}_${prefix}${field}` : `${prefix}${field}`;
    const getError = (field) => errors[getFieldName(field)];
    
    return (
      <InfoBlock>
        <BoldBlue>{isEn ? 'Designer Information' : 'Informations du designer'}</BoldBlue>
        <br/><br/>
        <FormGroup>
          <Label>{isEn ? 'Company name' : 'Nom de l\'entreprise'}<RequiredMark>*</RequiredMark></Label>
          <StyledInput
            type="text"
            value={getValue('CompanyName')}
            onChange={(e) => {
              if (pieceIndex !== null) {
                handlePieceChange(pieceIndex, `${prefix}CompanyName`, e.target.value);
              } else {
                handleInputChange({ target: { name: `${prefix}CompanyName`, value: e.target.value } });
              }
            }}
            style={{borderColor: getError('CompanyName') ? '#dc3545' : ''}}
          />
          {getError('CompanyName') && <ErrorMessage>{getError('CompanyName')}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>{isEn 
            ? 'Does the company have an international presence (through a showroom, distributor, trade show, exhibition, etc.)?'
            : 'L\'entreprise est-elle présente à l’internationale (à travers un showroom, un distributeur, un salon, une exposition, etc.) ?'}<RequiredMark>*</RequiredMark></Label>
          <StyledSelect
            value={getValue('InternationalPresence')}
            onChange={(e) => {
              if (pieceIndex !== null) {
                handlePieceChange(pieceIndex, `${prefix}InternationalPresence`, e.target.value);
              } else {
                handleInputChange({ target: { name: `${prefix}InternationalPresence`, value: e.target.value } });
              }
            }}
            style={{borderColor: getError('InternationalPresence') ? '#dc3545' : ''}}
          >
            <option value="">{isEn ? 'Select' : 'Sélectionnez'}</option>
            <option value="oui">{isEn ? 'Yes' : 'Oui'}</option>
            <option value="non">{isEn ? 'No' : 'Non'}</option>
          </StyledSelect>
          {getError('InternationalPresence') && <ErrorMessage>{getError('InternationalPresence')}</ErrorMessage>}
        </FormGroup>

        {getValue('InternationalPresence') === 'oui' && (
          <FormGroup>
            <Label>{isEn ? 'Please specify' : 'Veuillez préciser'}<RequiredMark>*</RequiredMark></Label>
            <StyledInput
              type="text"
              value={getValue('InternationalDetails')}
              onChange={(e) => {
                if (pieceIndex !== null) {
                  handlePieceChange(pieceIndex, `${prefix}InternationalDetails`, e.target.value);
                } else {
                  handleInputChange({ target: { name: `${prefix}InternationalDetails`, value: e.target.value } });
                }
              }}
              placeholder={isEn ? 'Showroom, distributor, trade show...' : 'Showroom, distributeur, salon...'}
              style={{borderColor: getError('InternationalDetails') ? '#dc3545' : ''}}
            />
            {getError('InternationalDetails') && <ErrorMessage>{getError('InternationalDetails')}</ErrorMessage>}
          </FormGroup>
        )}

        <FormGroup>
          <Label>{isEn 
            ? 'Does the company have an up-to-date website and active social media presence, including Instagram?'
            : 'L\'entreprise a-t-elle un site internet à jour, une activité sur les réseaux sociaux dont Instagram ?'}<RequiredMark>*</RequiredMark></Label>
          <StyledSelect
            value={getValue('WebAndSocial')}
            onChange={(e) => {
              if (pieceIndex !== null) {
                handlePieceChange(pieceIndex, `${prefix}WebAndSocial`, e.target.value);
              } else {
                handleInputChange({ target: { name: `${prefix}WebAndSocial`, value: e.target.value } });
              }
            }}
            style={{borderColor: getError('WebAndSocial') ? '#dc3545' : ''}}
          >
            <option value="">{isEn ? 'Select' : 'Sélectionnez'}</option>
            <option value="oui">{isEn ? 'Yes' : 'Oui'}</option>
            <option value="non">{isEn ? 'No' : 'Non'}</option>
          </StyledSelect>
          {getError('WebAndSocial') && <ErrorMessage>{getError('WebAndSocial')}</ErrorMessage>}
        </FormGroup>

        {getValue('WebAndSocial') === 'oui' && (
          <FormGroup>
            <Label>{isEn ? 'Please specify' : 'Veuillez préciser'}<RequiredMark>*</RequiredMark></Label>
            <StyledInput
              type="text"
              value={getValue('WebAndSocialDetails')}
              onChange={(e) => {
                if (pieceIndex !== null) {
                  handlePieceChange(pieceIndex, `${prefix}WebAndSocialDetails`, e.target.value);
                } else {
                  handleInputChange({ target: { name: `${prefix}WebAndSocialDetails`, value: e.target.value } });
                }
              }}
              placeholder={isEn ? 'Website URL, Instagram handle...' : 'URL du site, compte Instagram...'}
              style={{borderColor: getError('WebAndSocialDetails') ? '#dc3545' : ''}}
            />
            {getError('WebAndSocialDetails') && <ErrorMessage>{getError('WebAndSocialDetails')}</ErrorMessage>}
          </FormGroup>
        )}

        <FormGroup>
          <Label>{isEn ? 'Company presentation' : 'Texte de présentation'}<RequiredMark>*</RequiredMark></Label>
          <TextArea
            rows={4}
            value={getValue('Presentation')}
            onChange={(e) => {
              if (pieceIndex !== null) {
                handlePieceChange(pieceIndex, `${prefix}Presentation`, e.target.value);
              } else {
                handleInputChange({ target: { name: `${prefix}Presentation`, value: e.target.value } });
              }
            }}
            style={{borderColor: getError('Presentation') ? '#dc3545' : ''}}
          />
          {getError('Presentation') && <ErrorMessage>{getError('Presentation')}</ErrorMessage>}
        </FormGroup>
      </InfoBlock>
    );
  };

  // Component to render fabricant block
  const renderFabricantBlock = (prefix = 'fabricant', piece = null, pieceIndex = null) => {
    const getValue = (field) => piece ? piece[`${prefix}${field}`] : formData[`${prefix}${field}`];
    const getFieldName = (field) => pieceIndex !== null ? `piece_${pieceIndex}_${prefix}${field}` : `${prefix}${field}`;
    const getError = (field) => errors[getFieldName(field)];
    
    return (
      <InfoBlock>
        <BoldBlue>{isEn ? 'Manufacturer/Publisher Information' : 'Informations du fabricant/éditeur'}</BoldBlue>
        <br/><br/>
        <FormGroup>
          <Label>{isEn ? 'Company name' : 'Nom de l\'entreprise'}<RequiredMark>*</RequiredMark></Label>
          <StyledInput
            type="text"
            value={getValue('CompanyName')}
            onChange={(e) => {
              if (pieceIndex !== null) {
                handlePieceChange(pieceIndex, `${prefix}CompanyName`, e.target.value);
              } else {
                handleInputChange({ target: { name: `${prefix}CompanyName`, value: e.target.value } });
              }
            }}
            style={{borderColor: getError('CompanyName') ? '#dc3545' : ''}}
          />
          {getError('CompanyName') && <ErrorMessage>{getError('CompanyName')}</ErrorMessage>}
        </FormGroup>

        <FormGroup style={{marginBottom: '0px'}}>
          <Label>{isEn ? 'Is the company a CODIFAB contributor?' : 'L\'entreprise est-elle cotisante au CODIFAB ?'}<RequiredMark>*</RequiredMark></Label>
          <StyledSelect
            value={getValue('Codifab')}
            onChange={(e) => {
              if (pieceIndex !== null) {
                handlePieceChange(pieceIndex, `${prefix}Codifab`, e.target.value);
              } else {
                handleInputChange({ target: { name: `${prefix}Codifab`, value: e.target.value } });
              }
            }}
            style={{borderColor: getError('Codifab') ? '#dc3545' : ''}}
          >
            <option value="">{isEn ? 'Select' : 'Sélectionnez'}</option>
            <option value="oui">{isEn ? 'Yes' : 'Oui'}</option>
            <option value="non">{isEn ? 'No' : 'Non'}</option>
            <option value="ne_sais_pas">{isEn ? 'I don\'t know' : 'Je ne sais pas'}</option>
          </StyledSelect>
          {getError('Codifab') && <ErrorMessage>{getError('Codifab')}</ErrorMessage>}
        </FormGroup>
        
        <ExplanationText>
          {isEn 
            ? 'CODIFAB (Professional Committee for the Development of French Furniture and Wood Industries) manages funds from an allocated tax, a mandatory contribution paid by companies in the wood and furniture sectors, directly levied on their turnover.'
            : 'Le CODIFAB (Comité professionnel de Développement des Industries Françaises de l\'Ameublement et du Bois) est chargé de gérer les fonds issus de la taxe affectée, une contribution obligatoire payée par les entreprises des secteurs du bois et de l\'ameublement, directement prélevée sur leur chiffre d\'affaires.'}
        </ExplanationText>

        <FormGroup>
          <Label>{isEn ? 'Is the company a member of Ameublement français?' : 'L\'entreprise est-elle adhérente à l\'Ameublement français ?'}<RequiredMark>*</RequiredMark></Label>
          <StyledSelect
            value={getValue('Ameublement')}
            onChange={(e) => {
              if (pieceIndex !== null) {
                handlePieceChange(pieceIndex, `${prefix}Ameublement`, e.target.value);
              } else {
                handleInputChange({ target: { name: `${prefix}Ameublement`, value: e.target.value } });
              }
            }}
            style={{borderColor: getError('Ameublement') ? '#dc3545' : ''}}
          >
            <option value="">{isEn ? 'Select' : 'Sélectionnez'}</option>
            <option value="oui">{isEn ? 'Yes' : 'Oui'}</option>
            <option value="non">{isEn ? 'No' : 'Non'}</option>
            <option value="ne_sais_pas">{isEn ? 'I don\'t know' : 'Je ne sais pas'}</option>
          </StyledSelect>
          {getError('Ameublement') && <ErrorMessage>{getError('Ameublement')}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>{isEn 
            ? 'Does the company have an international presence (through a showroom, distributor, trade show, exhibition, etc.)?'
            : 'L\'entreprise est-elle présente à l’internationale (à travers un showroom, un distributeur, un salon, une exposition, etc.) ?'}<RequiredMark>*</RequiredMark></Label>
          <StyledSelect
            value={getValue('InternationalPresence')}
            onChange={(e) => {
              if (pieceIndex !== null) {
                handlePieceChange(pieceIndex, `${prefix}InternationalPresence`, e.target.value);
              } else {
                handleInputChange({ target: { name: `${prefix}InternationalPresence`, value: e.target.value } });
              }
            }}
            style={{borderColor: getError('InternationalPresence') ? '#dc3545' : ''}}
          >
            <option value="">{isEn ? 'Select' : 'Sélectionnez'}</option>
            <option value="oui">{isEn ? 'Yes' : 'Oui'}</option>
            <option value="non">{isEn ? 'No' : 'Non'}</option>
          </StyledSelect>
          {getError('InternationalPresence') && <ErrorMessage>{getError('InternationalPresence')}</ErrorMessage>}
        </FormGroup>

        {getValue('InternationalPresence') === 'oui' && (
          <FormGroup>
            <Label>{isEn ? 'Please specify' : 'Veuillez préciser'}<RequiredMark>*</RequiredMark></Label>
            <StyledInput
              type="text"
              value={getValue('InternationalDetails')}
              onChange={(e) => {
                if (pieceIndex !== null) {
                  handlePieceChange(pieceIndex, `${prefix}InternationalDetails`, e.target.value);
                } else {
                  handleInputChange({ target: { name: `${prefix}InternationalDetails`, value: e.target.value } });
                }
              }}
              placeholder={isEn ? 'Showroom, distributor, trade show...' : 'Showroom, distributeur, salon...'}
              style={{borderColor: getError('InternationalDetails') ? '#dc3545' : ''}}
            />
            {getError('InternationalDetails') && <ErrorMessage>{getError('InternationalDetails')}</ErrorMessage>}
          </FormGroup>
        )}

        <FormGroup>
          <Label>{isEn 
            ? 'Does the company have an up-to-date website and social media activity, including Instagram?'
            : 'L\'entreprise a-t-elle un site internet à jour, une activité sur les réseaux sociaux dont Instagram ?'}<RequiredMark>*</RequiredMark></Label>
          <StyledSelect
            value={getValue('WebAndSocial')}
            onChange={(e) => {
              if (pieceIndex !== null) {
                handlePieceChange(pieceIndex, `${prefix}WebAndSocial`, e.target.value);
              } else {
                handleInputChange({ target: { name: `${prefix}WebAndSocial`, value: e.target.value } });
              }
            }}
            style={{borderColor: getError('WebAndSocial') ? '#dc3545' : ''}}
          >
            <option value="">{isEn ? 'Select' : 'Sélectionnez'}</option>
            <option value="oui">{isEn ? 'Yes' : 'Oui'}</option>
            <option value="non">{isEn ? 'No' : 'Non'}</option>
          </StyledSelect>
          {getError('WebAndSocial') && <ErrorMessage>{getError('WebAndSocial')}</ErrorMessage>}
        </FormGroup>

        {getValue('WebAndSocial') === 'oui' && (
          <FormGroup>
            <Label>{isEn ? 'Please specify' : 'Veuillez préciser'}<RequiredMark>*</RequiredMark></Label>
            <StyledInput
              type="text"
              value={getValue('WebAndSocialDetails')}
              onChange={(e) => {
                if (pieceIndex !== null) {
                  handlePieceChange(pieceIndex, `${prefix}WebAndSocialDetails`, e.target.value);
                } else {
                  handleInputChange({ target: { name: `${prefix}WebAndSocialDetails`, value: e.target.value } });
                }
              }}
              placeholder={isEn ? 'Website URL, Instagram handle...' : 'URL du site, compte Instagram...'}
              style={{borderColor: getError('WebAndSocialDetails') ? '#dc3545' : ''}}
            />
            {getError('WebAndSocialDetails') && <ErrorMessage>{getError('WebAndSocialDetails')}</ErrorMessage>}
          </FormGroup>
        )}

        <FormGroup>
          <Label>{isEn ? 'Company presentation' : 'Texte de présentation'}<RequiredMark>*</RequiredMark></Label>
          <TextArea
            rows={4}
            value={getValue('Presentation')}
            onChange={(e) => {
              if (pieceIndex !== null) {
                handlePieceChange(pieceIndex, `${prefix}Presentation`, e.target.value);
              } else {
                handleInputChange({ target: { name: `${prefix}Presentation`, value: e.target.value } });
              }
            }}
            style={{borderColor: getError('Presentation') ? '#dc3545' : ''}}
          />
          {getError('Presentation') && <ErrorMessage>{getError('Presentation')}</ErrorMessage>}
        </FormGroup>
      </InfoBlock>
    );
  };

  return (
    <FormContainer>
      <Title>{isEn ? 'Milan 2026 Project' : 'Projet Milan 2026'}</Title>

      {validationError && (
        <Alert variant="danger" style={{ maxWidth: '800px', margin: '0 auto 25px' }}>
          {validationError}
        </Alert>
      )}

      {success ? (
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <Alert variant="success" style={{ maxWidth: '600px', margin: '0 auto 30px' }}>
            {isEn 
              ? 'Your application has been submitted successfully! You will receive a confirmation email.'
              : 'Votre candidature a été soumise avec succès! Vous allez recevoir un email de confirmation.'}
          </Alert>
        </div>
      ) : (
        <>
          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}

          {fullUser && (
            <>
              <Alert variant="info">
                {isEn 
                  ? 'Some fields have been pre-filled with your profile information. Please review and complete all fields.'
                  : 'Certains champs ont été pré-remplis avec vos informations de profil. Veuillez vérifier et compléter tous les champs.'}
              </Alert>
              <Alert variant="danger" style={{ marginTop: '15px' }}>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  <li>{isEn ? 'Pieces must be available between April 1st and May 17th, 2026' : 'Les pièces doivent être disponibles entre le 1er avril et le 17 mai 2026'}.</li>
                  <li>{isEn ? 'Pieces must be manufactured in France' : 'Les pièces doivent être fabriquées en France'}.</li>
                  <li>{isEn ? 'Pieces must be designed by a designer' : 'Les pièces doivent être conçues par un designer'}.</li>
                  <li>{isEn ? 'Pieces must be recent or in production (minimum April 2025)' : 'Les pièces doivent être récentes ou en cours de production (minimum avril 2025)'}.</li>
                </ul>
              </Alert>
            </>
          )}

          <form onSubmit={handlePreview}>
            <FormSection>
              <SectionTitle>{isEn ? 'Applicant Contact' : 'Contact du candidat'}</SectionTitle>

              <Row>
                <FormGroup>
                  <Label>{isEn ? 'First name' : 'Prénom'}<RequiredMark>*</RequiredMark></Label>
                  <StyledInput
                    ref={el => fieldRefs.current.firstName = el}
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder={isEn ? 'John' : 'Jean'}
                    style={{borderColor: errors.firstName ? '#dc3545' : ''}}
                  />
                  {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
                </FormGroup>
                <FormGroup>
                  <Label>{isEn ? 'Last name' : 'Nom'}<RequiredMark>*</RequiredMark></Label>
                  <StyledInput
                    ref={el => fieldRefs.current.lastName = el}
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder={isEn ? 'Doe' : 'Dupont'}
                    style={{borderColor: errors.lastName ? '#dc3545' : ''}}
                  />
                  {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
                </FormGroup>
              </Row>

              <FormGroup>
                <Label>{isEn ? 'Email' : 'Adresse mail'}<RequiredMark>*</RequiredMark></Label>
                <StyledInput
                  ref={el => fieldRefs.current.email = el}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={isEn ? 'your.email@example.com' : 'votre.email@exemple.com'}
                  style={{borderColor: errors.email ? '#dc3545' : ''}}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>{isEn ? 'Phone number' : 'Numéro de téléphone'}<RequiredMark>*</RequiredMark></Label>
                <StyledInput
                  ref={el => fieldRefs.current.phoneNumber = el}
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder={isEn ? '+1 234 567 8900' : '+33 1 23 45 67 89'}
                  style={{borderColor: errors.phoneNumber ? '#dc3545' : ''}}
                />
                {errors.phoneNumber && <ErrorMessage>{errors.phoneNumber}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>{isEn ? 'Are you a designer or manufacturer/publisher?' : 'Êtes-vous designer ou fabricant/éditeur ?'}<RequiredMark>*</RequiredMark></Label>
                <StyledSelect
                  ref={el => fieldRefs.current.userType = el}
                  name="userType"
                  value={formData.userType}
                  onChange={handleInputChange}
                  style={{borderColor: errors.userType ? '#dc3545' : ''}}
                >
                  <option value="">{isEn ? 'Select' : 'Sélectionnez'}</option>
                  <option value="designer">{isEn ? 'Designer' : 'Designer'}</option>
                  <option value="fabricant">{isEn ? 'Manufacturer/Publisher' : 'Fabricant/Éditeur'}</option>
                </StyledSelect>
                {errors.userType && <ErrorMessage>{errors.userType}</ErrorMessage>}
              </FormGroup>
            </FormSection>

            {/* Show designer or fabricant block based on user type */}
            {formData.userType === 'designer' && renderDesignerBlock()}
            {formData.userType === 'fabricant' && renderFabricantBlock()}
            
            {/* Show pieces section only after user selects their type */}
            {formData.userType && (<>
              <FormSection>
                <SectionTitle>{isEn ? 'Proposed pieces' : 'Pièces proposées'}</SectionTitle>
                {errors.pieces && (
                  <Alert variant="danger">{errors.pieces}</Alert>
                )}

                {formData.pieces.map((piece, index) => (
                  <PieceCard key={index} className="piece-card">
                    <PieceHeader 
                      isExpanded={expandedPieces[index]}
                      onClick={(e) => {
                        // Don't toggle if clicking remove button
                        if (!e.target.closest('button')) {
                          togglePieceExpansion(index);
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ExpandIcon isExpanded={expandedPieces[index]}>▶</ExpandIcon>
                        <PieceTitle>
                          {isEn ? `Piece ${index + 1}` : `Pièce ${index + 1}`}
                          {piece.name && <PieceName>- {piece.name}</PieceName>}
                        </PieceTitle>
                      </div>
                      {expandedPieces[index] && (
                        <RemoveButton type="button" onClick={() => removePiece(index)}>
                          {isEn ? 'Remove' : 'Supprimer'}
                        </RemoveButton>
                      )}
                    </PieceHeader>

                    <PieceContent isExpanded={expandedPieces[index]}>
                      <FormGroup>
                      <Label>{isEn ? 'Name' : 'Nom'}<RequiredMark>*</RequiredMark></Label>
                      <StyledInput
                        type="text"
                        value={piece.name}
                        onChange={(e) => handlePieceChange(index, 'name', e.target.value)}
                        placeholder={isEn ? 'Piece name' : 'Nom de la pièce'}
                        style={{borderColor: errors[`piece_${index}_name`] ? '#dc3545' : ''}}
                      />
                      {errors[`piece_${index}_name`] && <ErrorMessage>{errors[`piece_${index}_name`]}</ErrorMessage>}
                    </FormGroup>

                    <Row>
                      <FormGroup>
                        <Label>{isEn ? 'Typology' : 'Typologie'}<RequiredMark>*</RequiredMark></Label>
                        <StyledSelect
                          value={piece.typologie}
                          onChange={(e) => handlePieceChange(index, 'typologie', e.target.value)}
                          style={{borderColor: errors[`piece_${index}_typologie`] ? '#dc3545' : ''}}
                        >
                          <option value="">{isEn ? 'Select' : 'Sélectionnez'}</option>
                          {typologieObjetFD100(i18n.language).map(option => (
                            <option key={`objet-${option.value}`} value={option.value}>{option.label}</option>
                          ))}
                        </StyledSelect>
                        {errors[`piece_${index}_typologie`] && <ErrorMessage>{errors[`piece_${index}_typologie`]}</ErrorMessage>}
                      </FormGroup>

                      <FormGroup>
                        <Label>Dimensions<RequiredMark>*</RequiredMark></Label>
                        <StyledInput
                          type="text"
                          value={piece.dimensions}
                          onChange={(e) => handlePieceChange(index, 'dimensions', e.target.value)}
                          placeholder={isEn ? 'L 120 x W 80 x H 75 cm' : 'L 120 x l 80 x H 75 cm'}
                          style={{borderColor: errors[`piece_${index}_dimensions`] ? '#dc3545' : ''}}
                        />
                        {errors[`piece_${index}_dimensions`] && <ErrorMessage>{errors[`piece_${index}_dimensions`]}</ErrorMessage>}
                      </FormGroup>
                    </Row>

                    {piece.typologie === 'autre' && (
                      <FormGroup>
                        <Label>{isEn ? 'Please specify' : 'Veuillez préciser'}<RequiredMark>*</RequiredMark></Label>
                        <StyledInput
                          type="text"
                          value={piece.typologieOther}
                          onChange={(e) => handlePieceChange(index, 'typologieOther', e.target.value)}
                          placeholder={isEn ? 'Specify the typology...' : 'Précisez la typologie...'}
                          style={{borderColor: errors[`piece_${index}_typologieOther`] ? '#dc3545' : ''}}
                        />
                        {errors[`piece_${index}_typologieOther`] && <ErrorMessage>{errors[`piece_${index}_typologieOther`]}</ErrorMessage>}
                      </FormGroup>
                    )}

                    <Row>
                      <FormGroup>
                        <Label>{isEn ? 'Materials' : 'Matériaux'}<RequiredMark>*</RequiredMark></Label>
                        <StyledInput
                          type="text"
                          value={piece.materials}
                          onChange={(e) => handlePieceChange(index, 'materials', e.target.value)}
                          placeholder={isEn ? 'Wood, metal, fabric...' : 'Bois, métal, tissu...'}
                          style={{borderColor: errors[`piece_${index}_materials`] ? '#dc3545' : ''}}
                        />
                        {errors[`piece_${index}_materials`] && <ErrorMessage>{errors[`piece_${index}_materials`]}</ErrorMessage>}
                      </FormGroup>

                      <FormGroup>
                        <Label>{isEn ? 'Weight' : 'Poids'}</Label>
                        <StyledInput
                          type="text"
                          value={piece.poids}
                          onChange={(e) => handlePieceChange(index, 'poids', e.target.value)}
                          placeholder={isEn ? '5 kg' : '5 kg'}
                          style={{borderColor: errors[`piece_${index}_poids`] ? '#dc3545' : ''}}
                        />
                        {errors[`piece_${index}_poids`] && <ErrorMessage>{errors[`piece_${index}_poids`]}</ErrorMessage>}
                      </FormGroup>
                    </Row>

                    <FormGroup>
                      <Label>{isEn 
                        ? 'Specify the creation date of the piece (maximum 2 years old, i.e. from April 2024). Please specify if the piece is still being created.'
                        : 'Précisez la date de création de la pièce (maximum 2 ans d\'ancienneté soit à partir d\'avril 2024). Merci de préciser si la pièce est en cours de création.'}<RequiredMark>*</RequiredMark></Label>
                      <TextArea
                        rows={2}
                        value={piece.creationDate}
                        onChange={(e) => handlePieceChange(index, 'creationDate', e.target.value)}
                        placeholder={isEn ? 'e.g., September 2024 (in production)' : 'ex: Septembre 2024 (en cours de production)'}
                        style={{borderColor: errors[`piece_${index}_creationDate`] ? '#dc3545' : ''}}
                      />
                      {errors[`piece_${index}_creationDate`] && <ErrorMessage>{errors[`piece_${index}_creationDate`]}</ErrorMessage>}
                    </FormGroup>

                    {/* Designer block for piece */}
                    <div style={{ marginTop: '25px', marginBottom: '25px' }}>
                      {formData.userType === 'designer' ? (
                        <InfoBlock>
                          <BoldBlue>
                            {isEn ? 'Designer of the piece' : 'Designer de la pièce'}
                          </BoldBlue>
                      <br/><br/>
                          <FormGroup>
                            <Label>{isEn ? 'Company name' : 'Nom de l\'entreprise'}</Label>
                            <ReadOnlyField>{formData.designerCompanyName}</ReadOnlyField>
                          </FormGroup>
                        </InfoBlock>
                      ) : (
                        renderDesignerBlock('designer', piece, index)
                      )}
                    </div>

                    {/* Fabricant block for piece */}
                    <div style={{ marginTop: '25px', marginBottom: '25px' }}>
                      {formData.userType === 'fabricant' ? (
                        <InfoBlock>
                          <BoldBlue>
                            {isEn ? 'Manufacturer/Publisher of the piece' : 'Fabricant/Éditeur de la pièce'}
                          </BoldBlue>
                          <br/><br/>
                          <FormGroup>
                            <Label>{isEn ? 'Company name' : 'Nom de l\'entreprise'}</Label>
                            <ReadOnlyField>{formData.fabricantCompanyName}</ReadOnlyField>
                          </FormGroup>
                        </InfoBlock>
                      ) : (
                        renderFabricantBlock('fabricant', piece, index)
                      )}
                    </div>

                    <FormGroup>
                      <Label>{isEn ? 'Specify the craftsmanship of the piece' : 'Précisez le savoir-faire de la pièce'}<RequiredMark>*</RequiredMark></Label>
                      <TextArea
                        rows={2}
                        value={piece.savoirFaire}
                        onChange={(e) => handlePieceChange(index, 'savoirFaire', e.target.value)}
                        placeholder={isEn 
                          ? 'Describe the techniques, materials and know-how used...'
                          : 'Décrivez les techniques, matériaux et savoir-faire utilisés...'}
                        style={{borderColor: errors[`piece_${index}_savoirFaire`] ? '#dc3545' : ''}}
                      />
                      {errors[`piece_${index}_savoirFaire`] && <ErrorMessage>{errors[`piece_${index}_savoirFaire`]}</ErrorMessage>}
                    </FormGroup>


                    <FormGroup>
                      <Label>{isEn 
                        ? 'Specify how your piece fits into a sustainability approach (ecological, ethical, qualitative)'
                        : 'Précisez en quoi votre pièce s\'inscrit dans une démarche de durabilité (écologique, éthique, qualitative)'}<RequiredMark>*</RequiredMark></Label>
                      <TextArea
                        rows={3}
                        value={piece.durability}
                        onChange={(e) => handlePieceChange(index, 'durability', e.target.value)}
                        placeholder={isEn 
                          ? 'Sustainable materials, local production, durability, recycling...'
                          : 'Matériaux durables, production locale, durabilité, recyclage...'}
                        style={{borderColor: errors[`piece_${index}_durability`] ? '#dc3545' : ''}}
                      />
                      {errors[`piece_${index}_durability`] && <ErrorMessage>{errors[`piece_${index}_durability`]}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label>{isEn 
                        ? 'Presentation text of the piece'
                        : 'Texte de présentation de la pièce'}<RequiredMark>*</RequiredMark></Label>
                      <TextArea
                        rows={4}
                        value={piece.presentationText}
                        onChange={(e) => handlePieceChange(index, 'presentationText', e.target.value)}
                        placeholder={isEn 
                          ? 'Describe the piece, its concept, inspiration, and unique features...'
                          : 'Décrivez la pièce, son concept, son inspiration et ses caractéristiques uniques...'}
                        style={{borderColor: errors[`piece_${index}_presentationText`] ? '#dc3545' : ''}}
                      />
                      {errors[`piece_${index}_presentationText`] && <ErrorMessage>{errors[`piece_${index}_presentationText`]}</ErrorMessage>}
                    </FormGroup>



                    <PieceMediaUpload
                      value={piece.media || []}
                      onChange={(media) => handlePieceMediaChange(index, media)}
                      pieceIndex={index}
                      error={errors[`piece_${index}_media`] || errors[`piece_${index}_media_credits`]}
                    />
                    </PieceContent>

                  </PieceCard>
                ))}

                {formData.pieces.length < 3 && (
                  <AddButton type="button" onClick={addPiece} className="add-piece-button">
                    {isEn ? 'Add a piece' : 'Ajouter une pièce'}
                  </AddButton>
                )}
              </FormSection>
            
              {/* Certification checkbox */}
              <FormSection style={{ marginTop: '30px', paddingTop: '0px' }}>
                <CheckboxGroup>
                  <Checkbox
                    type="checkbox"
                    id="certification"
                    checked={certificationAccepted}
                    onChange={(e) => {
                      setCertificationAccepted(e.target.checked);
                      if (errors.certification) {
                        setErrors(prev => ({ ...prev, certification: '' }));
                      }
                    }}
                  />
                  <label htmlFor="certification" style={{ fontSize: '15px', lineHeight: '1.5' }}>
                    {isEn 
                      ? 'I certify that I have read and understood the eligibility conditions and selection criteria mentioned in the form. I am aware that this application does not guarantee my participation in the exhibition, as it is subject to selection by curators.'
                      : 'Je certifie avoir pris connaissance des conditions d\'éligibilité et des critères de sélection mentionnés dans le formulaire. Je suis conscient(e) que cette candidature ne garantit en aucun cas ma participation à l\'exposition, celle-ci étant soumise à une sélection effectuée par des curateurs.'}
                    <RequiredMark>*</RequiredMark>
                  </label>
                </CheckboxGroup>
                {errors.certification && <ErrorMessage style={{ marginTop: '10px', marginBottom: '20px' }}>{errors.certification}</ErrorMessage>}
                <br/>
                <span>{isEn ?
                  'For any questions or requests, please contact via email at: boury@via.fr'
                  : 'Pour toute question ou demande, veuillez contacter par mail l’adresse suivante : boury@via.fr'
                }</span>
              </FormSection>
            </>)}

            <SubmitButton type="submit" disabled={loading}>
              {loading ? <Spinner /> : (isEn ? 'Preview and submit' : 'Aperçu et soumettre')}
            </SubmitButton>
          </form>
        </>
      )}

      {showPreview && (
        <PreviewModal onClick={() => setShowPreview(false)}>
          <PreviewContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowPreview(false)}>×</CloseButton>
            
            <PreviewHeader>
              <PreviewTitle>{isEn ? 'Submission Preview' : 'Aperçu de la soumission'}</PreviewTitle>
            </PreviewHeader>

            <WarningBox>
              <strong>⚠️ {isEn ? 'Important' : 'Important'}</strong>
              {isEn 
                ? 'Once submitted, this form cannot be edited. Please review all information carefully before confirming.'
                : 'Une fois soumis, ce formulaire ne pourra plus être modifié. Veuillez vérifier attentivement toutes les informations avant de confirmer.'}
            </WarningBox>

            <PreviewSection>
              <PreviewSectionTitle>{isEn ? 'Contact Information' : 'Informations de contact'}</PreviewSectionTitle>
              
              <PreviewGrid>
                <PreviewLabel>{isEn ? 'First name:' : 'Prénom:'}</PreviewLabel>
                <PreviewValue>{formData.firstName}</PreviewValue>
                
                <PreviewLabel>{isEn ? 'Last name:' : 'Nom:'}</PreviewLabel>
                <PreviewValue>{formData.lastName}</PreviewValue>
                
                <PreviewLabel>Email:</PreviewLabel>
                <PreviewValue>{formData.email}</PreviewValue>
                
                <PreviewLabel>{isEn ? 'Phone number:' : 'Numéro de téléphone:'}</PreviewLabel>
                <PreviewValue>{formData.phoneNumber}</PreviewValue>
                
                <PreviewLabel>{isEn ? 'Type:' : 'Type:'}</PreviewLabel>
                <PreviewValue>{formData.userType === 'designer' ? 'Designer' : 'Fabricant/Éditeur'}</PreviewValue>
              </PreviewGrid>
            </PreviewSection>
            
            {/* Show designer block info */}
            {formData.userType === 'designer' && (
              <PreviewSection>
                <PreviewSectionTitle>{isEn ? 'Designer Information' : 'Informations du designer'}</PreviewSectionTitle>
                <PreviewGrid>
                  <PreviewLabel>{isEn ? 'Company name:' : 'Nom de l\'entreprise:'}</PreviewLabel>
                  <PreviewValue>{formData.designerCompanyName}</PreviewValue>
                  
                  <PreviewLabel>{isEn ? 'International presence:' : 'Présence internationale:'}</PreviewLabel>
                  <PreviewValue>{formData.designerInternationalPresence}</PreviewValue>
                  
                  {formData.designerInternationalDetails && (
                    <>
                      <PreviewLabel>{isEn ? 'International details:' : 'Détails internationaux:'}</PreviewLabel>
                      <PreviewValue>{formData.designerInternationalDetails}</PreviewValue>
                    </>
                  )}
                  
                  <PreviewLabel>{isEn ? 'Website and social media:' : 'Site web et réseaux sociaux:'}</PreviewLabel>
                  <PreviewValue>{formData.designerWebAndSocial}</PreviewValue>
                  
                  {formData.designerWebAndSocialDetails && (
                    <>
                      <PreviewLabel>{isEn ? 'Web/Social details:' : 'Détails web/réseaux:'}</PreviewLabel>
                      <PreviewValue>{formData.designerWebAndSocialDetails}</PreviewValue>
                    </>
                  )}
                  
                  <PreviewLabel>{isEn ? 'Presentation:' : 'Présentation:'}</PreviewLabel>
                  <PreviewValue>{formData.designerPresentation}</PreviewValue>
                </PreviewGrid>
              </PreviewSection>
            )}
            
            {/* Show fabricant block info */}
            {formData.userType === 'fabricant' && (
              <PreviewSection>
                <PreviewSectionTitle>{isEn ? 'Manufacturer/Publisher Information' : 'Informations du fabricant/éditeur'}</PreviewSectionTitle>
                <PreviewGrid>
                  <PreviewLabel>{isEn ? 'Company name:' : 'Nom de l\'entreprise:'}</PreviewLabel>
                  <PreviewValue>{formData.fabricantCompanyName}</PreviewValue>
                  
                  <PreviewLabel>{isEn ? 'CODIFAB contributor:' : 'Cotisant CODIFAB:'}</PreviewLabel>
                  <PreviewValue>{formData.fabricantCodifab}</PreviewValue>
                  
                  <PreviewLabel>{isEn ? 'Ameublement français member:' : 'Adhérent Ameublement français:'}</PreviewLabel>
                  <PreviewValue>{formData.fabricantAmeublement}</PreviewValue>
                  
                  <PreviewLabel>{isEn ? 'International presence:' : 'Présence internationale:'}</PreviewLabel>
                  <PreviewValue>{formData.fabricantInternationalPresence}</PreviewValue>
                  
                  {formData.fabricantInternationalDetails && (
                    <>
                      <PreviewLabel>{isEn ? 'International details:' : 'Détails internationaux:'}</PreviewLabel>
                      <PreviewValue>{formData.fabricantInternationalDetails}</PreviewValue>
                    </>
                  )}
                  
                  <PreviewLabel>{isEn ? 'Website and social media:' : 'Site web et réseaux sociaux:'}</PreviewLabel>
                  <PreviewValue>{formData.fabricantWebAndSocial}</PreviewValue>
                  
                  {formData.fabricantWebAndSocialDetails && (
                    <>
                      <PreviewLabel>{isEn ? 'Web/Social details:' : 'Détails web/réseaux:'}</PreviewLabel>
                      <PreviewValue>{formData.fabricantWebAndSocialDetails}</PreviewValue>
                    </>
                  )}
                  
                  <PreviewLabel>{isEn ? 'Presentation:' : 'Présentation:'}</PreviewLabel>
                  <PreviewValue>{formData.fabricantPresentation}</PreviewValue>
                </PreviewGrid>
              </PreviewSection>
            )}

            <PreviewSection>
              <PreviewSectionTitle>{isEn ? 'Proposed pieces' : 'Pièces proposées'} ({formData.pieces.length})</PreviewSectionTitle>
              
              {formData.pieces.map((piece, index) => (
                <PreviewPieceCard key={index}>
                  <h4>{isEn ? `Piece ${index + 1}` : `Pièce ${index + 1}`}: {piece.name}</h4>
                  
                  <PreviewGrid>
                    <PreviewLabel>{isEn ? 'Typology:' : 'Typologie:'}</PreviewLabel>
                    <PreviewValue>
                      {piece.typologie === 'autre' && piece.typologieOther 
                        ? piece.typologieOther 
                        : getTypologyLabel(piece.typologie)}
                    </PreviewValue>
                    
                    <PreviewLabel>{isEn ? 'Creation date:' : 'Date de création:'}</PreviewLabel>
                    <PreviewValue>{piece.creationDate}</PreviewValue>
                    
                    {/* Show designer info for piece */}
                    {formData.userType === 'designer' ? (
                      <>
                        <PreviewLabel>{isEn ? 'Designer:' : 'Designer:'}</PreviewLabel>
                        <PreviewValue>{formData.designerCompanyName}</PreviewValue>
                      </>
                    ) : piece.designerCompanyName && (
                      <>
                        <PreviewLabel>{isEn ? 'Designer:' : 'Designer:'}</PreviewLabel>
                        <PreviewValue>{piece.designerCompanyName}</PreviewValue>
                      </>
                    )}
                    
                    {/* Show fabricant info for piece */}
                    {formData.userType === 'fabricant' ? (
                      <>
                        <PreviewLabel>{isEn ? 'Manufacturer/Publisher:' : 'Fabricant/Éditeur:'}</PreviewLabel>
                        <PreviewValue>{formData.fabricantCompanyName}</PreviewValue>
                      </>
                    ) : piece.fabricantCompanyName && (
                      <>
                        <PreviewLabel>{isEn ? 'Manufacturer/Publisher:' : 'Fabricant/Éditeur:'}</PreviewLabel>
                        <PreviewValue>{piece.fabricantCompanyName}</PreviewValue>
                      </>
                    )}
                    
                    <PreviewLabel>{isEn ? 'Craftsmanship:' : 'Savoir-faire:'}</PreviewLabel>
                    <PreviewValue>{piece.savoirFaire}</PreviewValue>
                    
                    <PreviewLabel>{isEn ? 'Sustainability:' : 'Durabilité:'}</PreviewLabel>
                    <PreviewValue>{piece.durability}</PreviewValue>
                    
                    <PreviewLabel>{isEn ? 'Presentation text:' : 'Texte de présentation:'}</PreviewLabel>
                    <PreviewValue>{piece.presentationText}</PreviewValue>
                    
                    <PreviewLabel>{isEn ? 'Dimensions:' : 'Dimensions:'}</PreviewLabel>
                    <PreviewValue>{piece.dimensions}</PreviewValue>
                    
                    <PreviewLabel>{isEn ? 'Materials:' : 'Matériaux:'}</PreviewLabel>
                    <PreviewValue>{piece.materials}</PreviewValue>
                    
                    {piece.poids && (
                      <>
                        <PreviewLabel>{isEn ? 'Weight:' : 'Poids:'}</PreviewLabel>
                        <PreviewValue>{piece.poids}</PreviewValue>
                      </>
                    )}
                    
                  </PreviewGrid>
                  
                  {piece.media && piece.media.length > 0 && (
                    <>
                      <h5 style={{ marginTop: '20px', marginBottom: '10px' }}>
                        {isEn ? 'Uploaded media' : 'Médias uploadés'} ({piece.media.length})
                      </h5>
                      <PreviewMediaGrid>
                        {piece.media.map((mediaItem, idx) => (
                          <PreviewMediaItem key={idx}>
                            {mediaItem.file.type.startsWith('image/') ? (
                              <img src={URL.createObjectURL(mediaItem.file)} alt={mediaItem.file.name} />
                            ) : mediaItem.file.type.startsWith('video/') ? (
                              <div style={{ height: '100px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                🎥 {isEn ? 'Video' : 'Vidéo'}
                              </div>
                            ) : (
                              <div style={{ height: '100px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                📄 PDF
                              </div>
                            )}
                            <div className="media-name">{mediaItem.file.name}</div>
                            {mediaItem.credit && (
                              <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
                                {mediaItem.credit}
                              </div>
                            )}
                          </PreviewMediaItem>
                        ))}
                      </PreviewMediaGrid>
                    </>
                  )}
                </PreviewPieceCard>
              ))}
            </PreviewSection>

            <PreviewButtons>
              <button
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
                onClick={() => setShowPreview(false)}
              >
                {isEn ? 'Back to edit' : 'Retour à l\'édition'}
              </button>
              
              <button
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <Spinner /> : (isEn ? 'Confirm and submit' : 'Confirmer et soumettre')}
              </button>
            </PreviewButtons>
          </PreviewContent>
        </PreviewModal>
      )}
    </FormContainer>
  );
};

export default Milan2026Form;